import BODY_REGIONS_FALLBACK from "./bodyRegions.generated";
import LEGACY_DISPLAY_ANCHORS_DATA from "./legacyDisplayAnchors.json";

/**
 * The body-region vocabulary the whole dashboard reads from.
 *
 * The payload (regions + artwork + legacy map) is served by GET /api/body-regions
 * and cached in Redux/localStorage — see Stores/BodyRegions. Until that lands we
 * serve the bundled generated copy, so the map paints on a cold load and keeps
 * painting if the API is unreachable. `setActiveBodyRegions` swaps the live
 * payload in; every helper below reads it synchronously, which is what lets
 * plain (non-React) label lookups work anywhere in the app.
 */
let ACTIVE = BODY_REGIONS_FALLBACK;

export const getBodyRegions = () => ACTIVE;

export const setActiveBodyRegions = (payload) => {
  if (payload && payload.regions && payload.shapes && payload.order) {
    ACTIVE = payload;
  }
};

export const normaliseRegionKey = (key) =>
  String(key || "")
    .replace(/-/g, "_")
    .toLowerCase()
    .trim();

export const regionMeta = (key) =>
  ACTIVE.regions[normaliseRegionKey(key)] || null;

export const isCanonicalRegion = (key) => Boolean(regionMeta(key));

const humanise = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

/**
 * Where an old-vocabulary key is DRAWN. Position only — never a claim about what
 * the record is.
 *
 * The API deliberately refuses to map these onto a canonical region (see
 * BodyRegions::UNMAPPABLE_LEGACY): a `lower_back` wound may or may not be sacral,
 * and deciding that in code would move a CQC-notifiable pressure-ulcer count. But
 * a record we cannot place is a record a nurse never sees, so we still put it on
 * the map — at the spot the OLD map drew it, as a "needs site confirmation"
 * marker rather than a filled region, and it keeps its original key on click so
 * the nurse resolves it against the real record.
 *
 * The last six are old keys the API aliases neither way (the old map let you tap
 * a calf on the front view and a shin on the back); they get the same treatment.
 *
 * ── Why this lives in a JSON file, and why you cannot just add a key ──────────
 *
 * The ANCHOR is a client drawing decision (the API has no opinion on where an
 * unplaceable record should be pinned), but the KEY SET is not ours at all: it
 * must be exactly BodyRegions::UNMAPPABLE_LEGACY, which the payload ships as
 * `legacy.needs_confirmation`.
 *
 * It used to be a hand-written object right here — correct, but generated by
 * nobody and checked by nothing, so the next change to UNMAPPABLE_LEGACY would
 * have rotted it in silence: a newly-unmappable key would resolve to `unplaced`
 * and drop off the map entirely, taking its wounds with it.
 *
 * `generate-body-regions.js --check` (the same gate `npm start` / `npm run build`
 * run) now diffs this file's keys against `legacy.needs_confirmation` and asserts
 * every anchor is a real canonical region. Adding a key to UNMAPPABLE_LEGACY now
 * FAILS the build until a human decides where it is drawn — which is the only
 * safe party to decide it.
 */
export const LEGACY_DISPLAY_ANCHORS = LEGACY_DISPLAY_ANCHORS_DATA;

const legacyAliases = () => ACTIVE.legacy?.aliases || {};

/**
 * The canonical region a stored key means, or null when only a nurse can say.
 * Mirrors BodyRegions::canonicalise() — null is "unresolved", never "nowhere".
 */
export const canonicaliseRegionKey = (key) => {
  const k = normaliseRegionKey(key);
  if (isCanonicalRegion(k)) return k;
  return legacyAliases()[k] || null;
};

export const regionSide = (key) => {
  const k = normaliseRegionKey(key);
  if (/(^|_)left(_|$)/.test(k)) return "left";
  if (/(^|_)right(_|$)/.test(k)) return "right";
  return null;
};

/**
 * How a stored body_location key renders:
 *   canonical    → the region itself, filled and clickable
 *   alias        → an old key that maps cleanly; drawn as its canonical region
 *   unconfirmed  → an old key we refuse to place; drawn as a marker at its old
 *                  spot, flagged for a nurse
 *   unplaced     → no key we recognise at all; listed off-map so it is never lost
 *
 * `key` is always the ORIGINAL stored key — it is what we hand back on click and
 * what the feed is keyed by. `drawAs` is only where to put ink.
 */
export const resolveRegionKey = (key) => {
  const k = normaliseRegionKey(key);
  const side = regionSide(k);

  if (isCanonicalRegion(k)) {
    return {
      key: k,
      kind: "canonical",
      drawAs: k,
      side,
      view: regionMeta(k).view,
    };
  }

  const alias = legacyAliases()[k];
  if (alias && isCanonicalRegion(alias)) {
    return {
      key: k,
      kind: "alias",
      drawAs: alias,
      side: regionSide(alias),
      view: regionMeta(alias).view,
    };
  }

  const pin = LEGACY_DISPLAY_ANCHORS[k];
  if (pin && isCanonicalRegion(pin.anchor)) {
    return {
      key: k,
      kind: "unconfirmed",
      drawAs: pin.anchor,
      side,
      view: pin.view,
    };
  }

  return { key: k, kind: "unplaced", drawAs: null, side, view: null };
};

export const regionNeedsSiteConfirmation = (key) => {
  const kind = resolveRegionKey(key).kind;
  return kind === "unconfirmed" || kind === "unplaced";
};

/**
 * Display label for any stored key — canonical, legacy, or something we have
 * never seen. Old keys are labelled as their old selves, not as the canonical
 * region they sit near: calling a `lower_back` record "Sacrum" would assert the
 * very thing we are asking the nurse to confirm.
 */
export const bodyRegionLabel = (key) => {
  const k = normaliseRegionKey(key);
  const meta = regionMeta(k);
  if (meta) return meta.label;
  // A legacy key the API aliases cleanly (BodyRegions::canonicalise) reads as the
  // region it maps onto, never as its raw old self — `left_elbows` is "Left elbow",
  // not "Left elbows". Mirrors carecentre's regionLabel so the same record reads the
  // same on a phone and on the dashboard. Unmappable legacy keys still fall through to
  // humanise() and keep their honest "(old map)" caveat via bodyRegionDisplayLabel.
  const alias = legacyAliases()[k];
  const aliasMeta = alias ? regionMeta(alias) : null;
  if (aliasMeta) return aliasMeta.label;
  return humanise(k);
};

/** Label plus the honest caveat, for lists and tooltips. */
export const bodyRegionDisplayLabel = (key) => {
  const label = bodyRegionLabel(key);
  return regionNeedsSiteConfirmation(key) ? `${label} (old map)` : label;
};

export const isPressureSite = (key) => Boolean(regionMeta(key)?.pressure_site);

/** Canonical regions as picker options, in head-to-toe group order. */
export const bodyRegionOptions = () =>
  Object.entries(ACTIVE.regions).map(([value, meta]) => ({
    value,
    label: meta.label,
    group: meta.group,
    view: meta.view,
    pressure_site: meta.pressure_site,
  }));

export const bodyRegionGroups = () => ACTIVE.groups || [];

/**
 * The same options, bucketed into the payload's head-to-toe groups — what a
 * <Select> or a search list should render.
 *
 * 115 regions in one flat list is a list a nurse scrolls past; grouped, "Sacrum"
 * sits under "Sacral & buttocks" and "Left heel" under "Feet & ankles". Group
 * ORDER comes from the payload (`groups`), never from Object key order, and a
 * region whose group the payload does not list still comes back — an unlistable
 * region would be an unrecordable wound site.
 */
export const bodyRegionOptionGroups = () => {
  const options = bodyRegionOptions();
  const groups = bodyRegionGroups();
  const seen = new Set();
  const out = [];

  const bucket = (name) => {
    if (!seen.has(name)) {
      seen.add(name);
      out.push({ group: name, options: [] });
    }
    return out.find((entry) => entry.group === name);
  };

  groups.forEach((name) => bucket(name));
  options.forEach((option) =>
    bucket(option.group || "Other").options.push(option),
  );

  return out.filter((entry) => entry.options.length > 0);
};

/**
 * Which view(s) a stored key can be drawn on. Legacy keys inherit the view of
 * whatever they resolve to; an unplaced key belongs to neither.
 */
export const regionViews = (key) => {
  const resolved = resolveRegionKey(key);
  if (!resolved.view) return [];
  return resolved.view === "both" ? ["front", "back"] : [resolved.view];
};
